/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement } from 'react';
import type { ShopperProducts } from '@salesforce/storefront-next-runtime/scapi';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export const InventoryStatus = {
    IN_STOCK: 'in-stock',
    LOW_STOCK: 'low-stock',
    PRE_ORDER: 'pre-order',
    BACK_ORDER: 'back-order',
    OUT_OF_STOCK: 'out-of-stock',
    UNKNOWN: 'unknown',
} as const;

export type InventoryStatusType = (typeof InventoryStatus)[keyof typeof InventoryStatus];

interface InventoryMessageProps {
    product: ShopperProducts.schemas['Product'];
    currentVariant?: VariantWithInventory | null;
    className?: string;
    /**
     * Stock level at or below which the item is considered "low stock".
     * When stockLevel > 0 && stockLevel <= lowStockThreshold, shows a warning-colored message.
     */
    lowStockThreshold?: number;
    /**
     * Maximum stock level to display. Stock counts above this value will be capped.
     * For example, if maxStockDisplay is 99 and actual stock is 150, it will display 99.
     */
    maxStockDisplay?: number;
    /**
     * Whether to show unknown inventory status messages. Defaults to false.
     * When false, unknown status messages are visually hidden.
     */
    showUnknownStatus?: boolean;
    /**
     * Custom function to determine inventory status. If not provided, uses the default logic.
     * This allows customers to implement their own inventory status determination logic.
     * Should return UNKNOWN instead of null when inventory data is unavailable.
     */
    getInventoryStatus?: (
        product: ShopperProducts.schemas['Product'],
        currentVariant?: ShopperProducts.schemas['Variant'] | null
    ) => InventoryStatusType;
}

/**
 * Gets the inventory status based on product/variant data
 */
type VariantWithInventory = ShopperProducts.schemas['Variant'] & {
    inventory?: ShopperProducts.schemas['Inventory'];
};

function getInventoryStatus(
    product: ShopperProducts.schemas['Product'],
    currentVariant?: VariantWithInventory | null,
    lowStockThreshold = 0
): InventoryStatusType {
    // Use variant inventory if available, otherwise use product inventory
    const inventory = currentVariant?.inventory || product.inventory;

    if (!inventory) return InventoryStatus.UNKNOWN;

    const isOrderable = inventory.orderable;
    const stockLevel = inventory.ats || 0;

    if (!isOrderable) {
        return InventoryStatus.OUT_OF_STOCK;
    }

    if (inventory.preorderable) {
        return InventoryStatus.PRE_ORDER;
    }

    if (inventory.backorderable) {
        return InventoryStatus.BACK_ORDER;
    }

    if (stockLevel > 0 && stockLevel <= lowStockThreshold) {
        return InventoryStatus.LOW_STOCK;
    }

    if (stockLevel > 0) {
        return InventoryStatus.IN_STOCK;
    }

    return InventoryStatus.OUT_OF_STOCK;
}

type InventoryStatusInfo = { message: string; className: string };

/**
 * Gets the appropriate message and styling for inventory status.
 *
 * Uses semantic status tokens for consistent theming.
 */
function getInventoryMessage(
    status: InventoryStatusType,
    t: TFunction<'product'>,
    stockDisplay?: number | string
): InventoryStatusInfo {
    switch (status) {
        case InventoryStatus.IN_STOCK: {
            // Avoid "In stock (0 units)" while ATS is still settling (e.g. master 0 then variant 58).
            const showUnitCount =
                stockDisplay != null &&
                (typeof stockDisplay === 'string'
                    ? stockDisplay.endsWith('+') || Number(stockDisplay) > 0
                    : stockDisplay > 0);
            return {
                message: showUnitCount ? t('inStockCount', { stockDisplay }) : t('inStock'),
                className: 'text-status-positive',
            };
        }
        case InventoryStatus.LOW_STOCK:
            return {
                message: stockDisplay != null ? t('lowStockCount', { stockDisplay }) : t('lowStock'),
                className: 'text-status-warning',
            };
        case InventoryStatus.PRE_ORDER:
            return {
                message: t('preOrder'),
                className: 'text-status-info',
            };
        case InventoryStatus.BACK_ORDER:
            return {
                message: t('backOrder'),
                className: 'text-status-warning',
            };
        case InventoryStatus.OUT_OF_STOCK:
            return {
                message: t('outOfStockLabel'),
                className: 'text-status-critical',
            };
        case InventoryStatus.UNKNOWN:
        default:
            return {
                message: 'Inventory unavailable',
                className: 'text-muted-foreground',
            };
    }
}

/**
 * Inventory Message Component
 *
 * Displays inventory status messages for products on the PDP:
 * - In stock: Green message
 * - Pre-order: Blue message
 * - Back order: Orange message
 * - Out of stock: Red message
 */
export default function InventoryMessage({
    product,
    currentVariant,
    className,
    lowStockThreshold = 0,
    maxStockDisplay = 99,
    showUnknownStatus = false,
    getInventoryStatus: customGetInventoryStatus,
}: InventoryMessageProps): ReactElement {
    const { t } = useTranslation('product');
    const inventory = currentVariant?.inventory || product.inventory;
    const stockLevel = inventory?.ats;
    const displayStock = stockLevel != null && stockLevel > maxStockDisplay ? `${maxStockDisplay}+` : stockLevel;

    let status = customGetInventoryStatus
        ? customGetInventoryStatus(product, currentVariant)
        : getInventoryStatus(product, currentVariant, lowStockThreshold);

    const hasVariants = (product.variants?.length ?? 0) > 0;
    const effectiveDisplay: number | string | undefined = displayStock;

    if (!customGetInventoryStatus && hasVariants) {
        const awaitingVariant = currentVariant == null;
        const variantResolvedWithoutInventory = currentVariant != null && currentVariant.inventory == null;

        // Hide misleading master "out of stock" until a variant is chosen
        if (awaitingVariant && status === InventoryStatus.OUT_OF_STOCK) {
            status = InventoryStatus.UNKNOWN;
        }

        if (variantResolvedWithoutInventory) {
            if (currentVariant.orderable === false) {
                status = InventoryStatus.OUT_OF_STOCK;
            } else if (currentVariant.orderable === true && status === InventoryStatus.OUT_OF_STOCK) {
                // Master rollup can say OOS while the selected variant is orderable and inventory is not on the variant yet
                status = InventoryStatus.IN_STOCK;
            }
        }
    }

    const statusInfo = getInventoryMessage(status, t, effectiveDisplay);
    const isUnknown = status === InventoryStatus.UNKNOWN;

    return (
        <div
            className={cn('flex items-center gap-2', className, isUnknown && !showUnknownStatus && 'hidden')}
            {...(isUnknown && !showUnknownStatus && { 'aria-hidden': true })}>
            <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-none bg-current', statusInfo.className)} />
            <p className={cn('text-sm font-medium', statusInfo.className)}>{statusInfo.message}</p>
        </div>
    );
}
